import client from "../auth/client";
import { Auth } from "../auth/auth";
import { bulkClauseApiPropForLatestUpload } from "../modules/vscDashboard/constants";

export const archiveContractReviewDocuments = async (reqBody: {
  vertical_id: string;
}) => {
  const res = await client.post("/doc_review/reset_contract_reviews/", reqBody);
  return res.data;
};

export const markDocumentAsCompleted = async (docId: string) => {
  const res = await client.post("/doc_review/set_doc_status/", {
    doc_id: docId,
    doc_status: "review_completed",
  });

  return res.data;
};

export const uploadFilesForClauseCheck = async (reqBody: FormData) => {
  const res = await client.post("/clause_check/multi_upload/", reqBody, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
};

export const extractMetaDataOfDocumentForClauseCheck = async (reqBody: {
  doc_id: string;
}) => {
  const res = await client.post("/clause_check/extract_doc_metadata/", reqBody);
  return res.data;
};

export const getClauseCheckDataForDashboard = async () => {
  const res = await client.get("/clause_check/files_stats/");
  return res.data;
};

export const getKccAgenticClauseCheckDataForDashboard = async () => {
  const res = await client.get(
    "/clause_check/v3/files_stats/?dashboard_type=kcc dashboard"
  );
  return res.data;
};
export const getDueContractsCount = async () => {
  const res = await client.get(
    "/dashboard/due_contract/get_due_contract_counts/"
  );
  return res.data;
};

export const getClauseCheckDocumentsDataForDashboard = async (
  reqBody: {
    clause_status: string[];
    page_size?: number;
    page?: number;
  } = {
    clause_status: bulkClauseApiPropForLatestUpload.clause_status,
    page_size: 10,
    page: 1,
  }
) => {
  const res = await client.post(
    `/clause_check/list_clause_check_contracts/?page_size=${reqBody.page_size}&page=${reqBody.page}`,
    reqBody
  );
  return res.data;
};

export const fetchClauseCheckDocumentsData = async (
  reqBody: {
    apiProps: {};
    queryParams: {
      page_size?: number;
      page?: number;
    };
  } = {
    apiProps: {},
    queryParams: {
      page_size: 10,
      page: 1,
    },
  }
) => {
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/clause_check/list_clause_check_contracts/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res.data;
};

export const getKCCBatches = async (
  reqBody: {
    apiProps: {
      vertical_id?: string;
    };
    queryParams: {
      page_size?: number;
      page?: number;
    };
  } = {
    apiProps: {},
    queryParams: {
      page_size: 10,
      page: 1,
    },
  }
) => {
  const { page, page_size } = reqBody.queryParams;
  const { vertical_id } = reqBody.apiProps;

  let url = `clause_check/v2/batch_jobs/?page=${page}&page_size=${page_size}`;
  if (vertical_id) {
    url += `&vertical_id=${vertical_id}`;
  }

  const res = await client.get(url);
  return res.data;
};

export const getKCCBatchDetails = async (
  reqBody: {
    apiProps: {
      batch_id?: string;
      vertical_id?: string;
      clause_status?: string[];
    };
    queryParams: {
      page_size?: number;
      page?: number;
    };
  } = {
    apiProps: {
      batch_id: "",
      vertical_id: "",
      clause_status: bulkClauseApiPropForLatestUpload.clause_status,
    },
    queryParams: {
      page_size: 10,
      page: 1,
    },
  }
) => {
  const res = await client.post(
    `/clause_check/v2/list_clause_check_contracts/`,
    reqBody.apiProps
  );
  return res.data;
};

export const initiateClauseDataExtraction = async (reqBody: {
  doc_id: string;
}) => {
  const res = await client.post("/clause_check/clause_check/", reqBody);
  return res.data;
};

export const archiveClauseCheckDocuments = async (reqBody: {
  doc_id: [];
  update_for_all: string;
  status: string;
  vertical_id: string;
}) => {
  const res = await client.post("/clause_check/update_doc_status/", reqBody);
  return res.data;
};

export const uploadFileOrFoldersForClauseCheckAdvanced = async (
  reqBody: FormData
) => {
  const res = await client.post("/clause_check/v2/batch_upload/", reqBody, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
};

export const getKCCAgenticFiles = async (
  reqBody: {
    apiProps: {
      vertical_id?: string;
      search_text?: string;
    };
    queryParams: {
      page_size?: number;
      page?: number;
    };
  } = {
    apiProps: {},
    queryParams: {
      page_size: 10,
      page: 1,
    },
  }
) => {
  const { page, page_size } = reqBody.queryParams;
  const { vertical_id, search_text } = reqBody.apiProps;

  let url = `clause_check/v3/get_files_by_clauses/?page=${page}&page_size=${page_size}`;
  if (vertical_id) {
    url += `&vertical_id=${vertical_id}`;
  }
  if (search_text && search_text.length >= 3) {
    url += `&search=${encodeURIComponent(search_text)}`;
  }

  const res = await client.get(url);
  return res.data;
};

// ============== V2 API for Agentic Dashboard ==============

export const getAboutToExpireContractsCountV2 = async () => {
  const res = await client.get(
    "/dashboard/due_contract/get_due_contract_counts/v2/"
  );
  return res.data;
};

export const getExpiredContractsCountV2 = async () => {
  const res = await client.get(
    "/dashboard/due_contract/get_expired_contract_counts/v2/"
  );
  return res.data;
};

// ============== KCC Agentic Download APIs ==============

export const downloadKCCAgenticOverviewCSV = async (reqBody: {
  vertical_id?: string;
  search_text?: string;
  status?: string;
}) => {
  const res = await client.post("/clause_check/v3/download_overview/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

export const downloadKCCAgenticClauseAnalysisCSV = async (reqBody: {
  vertical_id?: string;
  search_text?: string;
  selected_columns?: string[];
  status?: string;
}) => {
  const res = await client.post("/clause_check/v3/download_clause_analysis/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

export const getKCCFileCount = async (
  verticalId?: string,
  status?: "active" | "expired" | "all",
) => {
  // status forwards the active/expired/all dropdown so the header above the
  // KCC table matches the rows the user actually sees. Same wire format as
  // the CRD header (both hit /clause_check/v3/file_count/ via VerticalFilesCountViewSet).
  const qs = new URLSearchParams();
  if (verticalId) qs.set("vertical_id", verticalId);
  if (status) qs.set("status", status);
  const res = await client.get(`/clause_check/v3/file_count/?${qs.toString()}`);
  return res?.data;
};

export const getKCCHierarchyFiles = async (params: {
  vertical_id?: string;
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string[];
  status?: string;
  agreement_type?: string;
  days_to_expire?: number | null;
  effective_date_from?: string | null;
  effective_date_to?: string | null;
  end_date_from?: string | null;
  end_date_to?: string | null;
}) => {
  const qs = new URLSearchParams();
  if (params.vertical_id) qs.set("vertical_id", params.vertical_id);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 10));
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  if (params.agreement_type) qs.set("agreement_type", params.agreement_type);
  if (params.days_to_expire != null) qs.set("days_to_expire", String(params.days_to_expire));
  if (params.effective_date_from) qs.set("effective_date_from", params.effective_date_from);
  if (params.effective_date_to) qs.set("effective_date_to", params.effective_date_to);
  if (params.end_date_from) qs.set("end_date_from", params.end_date_from);
  if (params.end_date_to) qs.set("end_date_to", params.end_date_to);
  // Multi-column sort: each sort_by value appended separately
  (params.sort_by || []).forEach((s) => qs.append("sort_by", s));
  const res = await client.get(
    `/clause_check/v3/get_files_by_hierarchy/?${qs.toString()}`
  );
  return res?.data;
};
